const { fromYMD, toYMD } = require('@/functions/date');

/**
 * DateList contains list of dates (YYYY-MM-DD) and provides methods to get the first, last, next, and previous date.
 */
class DateList {
    /**
     * @param {Date[]} dates
     */
    constructor(dates) {
        /**
         * @type {string[]}
         */
        this.dateStrings = dates.map((d) => toYMD(d));
        // Remove duplicates using Set
        this.dateStrings = [...new Set(this.dateStrings)];
        this.dateStrings.sort();

        this.dates = dates;
    }

    /**
     * @returns {number}
     */
    getCount() {
        return this.dateStrings.length;
    }

    /**
     * @returns {Date|null}
     */
    getFirstDate() {
        return this.dateStrings[0] ? fromYMD(this.dateStrings[0]) : null;
    }

    /**
     * @param {Date} date
     * @returns {boolean}
     */
    hasDate(date) {
        const dateString = toYMD(date);

        return this.dateStrings.indexOf(dateString) !== -1;
    }

    /**
     * Returns the date in the list that comes after the supplied date.
     *
     * @param {Date} fromDate
     * @param {boolean} returnSameDate If true it will return the supplied date if it is in the list, or the next
     *      closest date in the future.
     *      If false (default) it will always return a date *after* the supplied date.
     *
     * @returns {Date|null}
     */
    getNextDate(fromDate, returnSameDate = false) {
        const dateString = toYMD(fromDate);
        const index = this.dateStrings.indexOf(dateString);
        if (index !== -1) {
            if (returnSameDate) {
                return fromYMD(this.dateStrings[index]);
            }

            // The date string of the fromDate is in the list. Just return the next index.
            return this.dateStrings[index + 1] ? fromYMD(this.dateStrings[index + 1]) : null;
        }

        // Iterate through all the date strings and compare them to the fromDate.
        // If the date string is >= the fromDate string, this date in the list is after the given date.
        // Because the list of date strings is sorted this must be the first date after the given date, so return that.
        for (let i = 0; i < this.dateStrings.length; i++) {
            if (this.dateStrings[i] >= dateString) {
                return fromYMD(this.dateStrings[i]);
            }
        }

        return null;
    }

    /**
     * @param {Date} fromDate
     * @returns {Date|null}
     */
    getPreviousDate(fromDate) {
        const dateString = toYMD(fromDate);
        const index = this.dateStrings.indexOf(dateString);
        if (index !== -1) {
            return this.dateStrings[index - 1] ? fromYMD(this.dateStrings[index - 1]) : null;
        }

        // Iterate backwards through all the date strings and compare them to the fromDate.
        // If the date string is < the fromDate string, this date in the list is before the given date.
        // Because the list of date strings is sorted this must be the first date before the given date, so return that.
        for (let i = this.dateStrings.length - 1; i >= 0; i--) {
            if (this.dateStrings[i] < dateString) {
                return fromYMD(this.dateStrings[i]);
            }
        }

        return null;
    }
}

module.exports = DateList;
